import type { Cliente, Gasto, Lote, Producto, Usuario, Venta } from '../types'
import { estadoStock, inicioAno, inicioDia, inicioMes, inicioSemana } from './format'

/** Las ventas anuladas no cuentan en ninguna métrica: existen solo como
 *  registro histórico. */
export const vigentes = (ventas: Venta[]) => ventas.filter((v) => v.estado !== 'anulada')

export const desde = (ventas: Venta[], fecha: Date) =>
  vigentes(ventas).filter((v) => new Date(v.fecha) >= fecha)

export const entre = (ventas: Venta[], inicio: Date, fin: Date) =>
  vigentes(ventas).filter((v) => {
    const f = new Date(v.fecha)
    return f >= inicio && f <= fin
  })

export const suma = (ventas: Venta[]) => ventas.reduce((s, v) => s + v.total, 0)
export const sumaUtilidad = (ventas: Venta[]) => ventas.reduce((s, v) => s + v.utilidad, 0)

export interface Periodo {
  clave: 'dia' | 'semana' | 'mes' | 'ano'
  etiqueta: string
  inicio: Date
}

export const PERIODOS: Periodo[] = [
  { clave: 'dia', etiqueta: 'Hoy', inicio: inicioDia() },
  { clave: 'semana', etiqueta: 'Esta semana', inicio: inicioSemana() },
  { clave: 'mes', etiqueta: 'Este mes', inicio: inicioMes() },
  { clave: 'ano', etiqueta: 'Este año', inicio: inicioAno() },
]

/** Compara un periodo con el inmediatamente anterior de igual duración.
 *  Sin comparación, un número solo dice cuánto; con ella dice si va
 *  bien o mal. */
export function variacion(ventas: Venta[], inicio: Date) {
  const ahora = new Date()
  const duracion = ahora.getTime() - inicio.getTime()
  const actual = suma(entre(ventas, inicio, ahora))
  const previo = suma(entre(ventas, new Date(inicio.getTime() - duracion), inicio))
  if (previo === 0) return { actual, previo, delta: actual > 0 ? 100 : 0 }
  return { actual, previo, delta: ((actual - previo) / previo) * 100 }
}

export function resumenInventario(productos: Producto[], umbralGlobal: number) {
  const activos = productos.filter((p) => p.activo)
  let unidades = 0
  let valorCosto = 0
  let valorVenta = 0
  let pocos = 0
  let agotados = 0

  for (const p of activos) {
    unidades += p.stock
    valorCosto += p.stock * p.precioCosto
    valorVenta += p.stock * p.precioSugerido
    const e = estadoStock(p, umbralGlobal)
    if (e === 'poco') pocos++
    if (e === 'agotado') agotados++
  }

  return {
    productos: activos.length,
    unidades,
    valorCosto,
    valorVenta,
    pocos,
    agotados,
    disponibles: activos.length - pocos - agotados,
  }
}

/** Serie diaria para gráficos: rellena los días sin ventas con cero,
 *  si no la línea miente sobre la continuidad. */
export function serieDiaria(ventas: Venta[], dias: number) {
  const mapa = new Map<string, { total: number; utilidad: number; ventas: number }>()
  for (let i = dias - 1; i >= 0; i--) {
    const d = new Date()
    d.setDate(d.getDate() - i)
    mapa.set(claveDia(d), { total: 0, utilidad: 0, ventas: 0 })
  }
  const limite = new Date()
  limite.setDate(limite.getDate() - dias + 1)
  for (const v of desde(ventas, inicioDia(limite))) {
    const k = claveDia(new Date(v.fecha))
    const actual = mapa.get(k)
    if (!actual) continue
    actual.total += v.total
    actual.utilidad += v.utilidad
    actual.ventas += 1
  }
  return [...mapa.entries()].map(([fecha, datos]) => ({ fecha, ...datos }))
}

export function serieMensual(ventas: Venta[], meses: number) {
  const mapa = new Map<string, { total: number; utilidad: number; ventas: number }>()
  const etiquetas = new Map<string, string>()
  const fmt = new Intl.DateTimeFormat('es-CL', { month: 'short' })
  for (let i = meses - 1; i >= 0; i--) {
    const d = new Date()
    d.setDate(1)
    d.setMonth(d.getMonth() - i)
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    mapa.set(k, { total: 0, utilidad: 0, ventas: 0 })
    etiquetas.set(k, fmt.format(d).replace('.', ''))
  }
  for (const v of vigentes(ventas)) {
    const d = new Date(v.fecha)
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const actual = mapa.get(k)
    if (!actual) continue
    actual.total += v.total
    actual.utilidad += v.utilidad
    actual.ventas += 1
  }
  return [...mapa.entries()].map(([k, datos]) => ({ fecha: etiquetas.get(k)!, ...datos }))
}

const claveDia = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`

export interface RankingProducto {
  productoId: string
  nombre: string
  codigo: string
  unidades: number
  ingresos: number
  utilidad: number
}

export function rankingProductos(ventas: Venta[], productos: Producto[]): RankingProducto[] {
  const mapa = new Map<string, RankingProducto>()
  for (const v of vigentes(ventas)) {
    for (const l of v.lineas) {
      const actual = mapa.get(l.productoId) ?? {
        productoId: l.productoId,
        nombre: l.nombre,
        codigo: l.codigo,
        unidades: 0,
        ingresos: 0,
        utilidad: 0,
      }
      actual.unidades += l.cantidad
      actual.ingresos += l.subtotal
      actual.utilidad += (l.precioUnitario - l.costoUnitario) * l.cantidad
      mapa.set(l.productoId, actual)
    }
  }
  // Los productos sin ninguna venta también importan: son los que hay
  // que dejar de comprar.
  for (const p of productos) {
    if (!mapa.has(p.id) && p.activo) {
      mapa.set(p.id, {
        productoId: p.id,
        nombre: p.nombre,
        codigo: p.codigo,
        unidades: 0,
        ingresos: 0,
        utilidad: 0,
      })
    }
  }
  return [...mapa.values()].sort((a, b) => b.unidades - a.unidades)
}

export interface RankingCliente {
  clienteId: string
  nombre: string
  tipo: Cliente['tipo']
  compras: number
  total: number
  ultima: string
  promedio: number
}

export function rankingClientes(ventas: Venta[]): RankingCliente[] {
  const mapa = new Map<string, RankingCliente>()
  for (const v of vigentes(ventas)) {
    const actual = mapa.get(v.clienteId) ?? {
      clienteId: v.clienteId,
      nombre: v.clienteNombre,
      tipo: v.clienteTipo,
      compras: 0,
      total: 0,
      ultima: v.fecha,
      promedio: 0,
    }
    actual.compras += 1
    actual.total += v.total
    if (v.fecha > actual.ultima) actual.ultima = v.fecha
    mapa.set(v.clienteId, actual)
  }
  return [...mapa.values()]
    .map((c) => ({ ...c, promedio: c.total / c.compras }))
    .sort((a, b) => b.total - a.total)
}

export interface RankingProveedor {
  proveedor: string
  entregas: number
  productosDistintos: number
  unidades: number
  total: number
  ultima: string
}

/** Un lote por proveedor, agrupado. "Entregas" cuenta días distintos en
 *  que llegó mercadería de ese proveedor —no lotes—, porque un mismo
 *  camión suele traer varios productos a la vez y cada uno queda como
 *  un lote separado (ver `registrarOrdenIngreso`); contar lotes
 *  inflaría el número de veces que realmente vino un camión. Los lotes
 *  sin proveedor registrado quedan fuera: no hay a quién atribuirlos. */
export function rankingProveedores(lotes: Lote[]): RankingProveedor[] {
  const mapa = new Map<
    string,
    { entregas: Set<string>; productos: Set<string>; unidades: number; total: number; ultima: string }
  >()
  for (const l of lotes) {
    const nombre = l.proveedor?.trim()
    if (!nombre) continue
    const actual = mapa.get(nombre) ?? {
      entregas: new Set<string>(),
      productos: new Set<string>(),
      unidades: 0,
      total: 0,
      ultima: l.ingresado,
    }
    actual.entregas.add(l.ingresado.slice(0, 10))
    actual.productos.add(l.productoId)
    actual.unidades += l.cantidadInicial
    actual.total += l.cantidadInicial * l.costo
    if (l.ingresado > actual.ultima) actual.ultima = l.ingresado
    mapa.set(nombre, actual)
  }
  return [...mapa.entries()]
    .map(([proveedor, p]) => ({
      proveedor,
      entregas: p.entregas.size,
      productosDistintos: p.productos.size,
      unidades: p.unidades,
      total: p.total,
      ultima: p.ultima,
    }))
    .sort((a, b) => b.total - a.total)
}

export interface RankingVendedor {
  usuarioId: string
  nombre: string
  ventas: number
  total: number
  utilidad: number
  comision: number
  ticket: number
}

export function rankingVendedores(ventas: Venta[], usuarios: Usuario[]): RankingVendedor[] {
  const mapa = new Map<string, RankingVendedor>()
  // NovaBerry (administración) y Despachos son cuentas de sistema, no
  // vendedores: no aparecen en el ranking aunque nunca hayan vendido.
  for (const u of usuarios.filter((u) => u.rol === 'vendedor')) {
    mapa.set(u.id, {
      usuarioId: u.id,
      nombre: u.nombre,
      ventas: 0,
      total: 0,
      utilidad: 0,
      comision: 0,
      ticket: 0,
    })
  }
  for (const v of vigentes(ventas)) {
    const actual =
      mapa.get(v.usuarioId) ??
      ({
        usuarioId: v.usuarioId,
        nombre: v.usuarioNombre,
        ventas: 0,
        total: 0,
        utilidad: 0,
        comision: 0,
        ticket: 0,
      } as RankingVendedor)
    actual.ventas += 1
    actual.total += v.total
    actual.utilidad += v.utilidad
    actual.comision += v.comision ?? 0
    mapa.set(v.usuarioId, actual)
  }
  return [...mapa.values()]
    .map((v) => ({ ...v, ticket: v.ventas ? v.total / v.ventas : 0 }))
    .filter((v) => v.ventas > 0)
    .sort((a, b) => b.total - a.total)
}

/** Todo lo que necesita el perfil de un vendedor: sus ventas, y por
 *  cada cliente que atiende, cuándo fue la última entrega real —no la
 *  fecha de venta, la de cuando el pedido efectivamente salió— para
 *  poder avisar si lleva mucho tiempo sin visitarlo. */
export interface ClienteDeVendedor {
  clienteId: string
  nombre: string
  tipo: Cliente['tipo']
  compras: number
  total: number
  ultimaCompra: string
  ultimaEntrega?: string
  diasSinEntrega: number | null
  seguimientoPendiente: boolean
}

export function clientesDeVendedor(
  ventas: Venta[],
  usuarioId: string,
  diasAviso = 7,
): ClienteDeVendedor[] {
  const mapa = new Map<string, ClienteDeVendedor>()
  const suyas = vigentes(ventas).filter((v) => v.usuarioId === usuarioId)
  for (const v of suyas) {
    const actual = mapa.get(v.clienteId) ?? {
      clienteId: v.clienteId,
      nombre: v.clienteNombre,
      tipo: v.clienteTipo,
      compras: 0,
      total: 0,
      ultimaCompra: v.fecha,
      ultimaEntrega: undefined,
      diasSinEntrega: null,
      seguimientoPendiente: false,
    }
    actual.compras += 1
    actual.total += v.total
    if (v.fecha > actual.ultimaCompra) actual.ultimaCompra = v.fecha
    // "fechaEntrega" queda guardada aunque después se revierta el
    // estado (para no perder la fecha real de la primera entrega), así
    // que acá también hay que exigir que siga "entregada" ahora mismo
    // —si no, una entrega corregida hacia atrás seguiría contando como
    // la última entrega real para el aviso de seguimiento.
    if (
      v.fechaEntrega &&
      v.estado === 'entregada' &&
      (!actual.ultimaEntrega || v.fechaEntrega > actual.ultimaEntrega)
    ) {
      actual.ultimaEntrega = v.fechaEntrega
    }
    mapa.set(v.clienteId, actual)
  }
  const hoy = Date.now()
  return [...mapa.values()]
    .map((c) => {
      const dias = c.ultimaEntrega
        ? Math.floor((hoy - new Date(c.ultimaEntrega).getTime()) / 86_400_000)
        : null
      return { ...c, diasSinEntrega: dias, seguimientoPendiente: dias !== null && dias >= diasAviso }
    })
    .sort((a, b) => b.total - a.total)
}

/** Las ventas de un vendedor con su comisión, más recientes primero —
 *  lo que alimenta la sección "Comisiones" de su perfil. */
export function ventasDeVendedor(ventas: Venta[], usuarioId: string) {
  return vigentes(ventas)
    .filter((v) => v.usuarioId === usuarioId)
    .sort((a, b) => b.fecha.localeCompare(a.fecha))
}

/** Historial de un cliente con los agregados que se muestran en su ficha. */
export function historialCliente(ventas: Venta[], clienteId: string) {
  const suyas = vigentes(ventas)
    .filter((v) => v.clienteId === clienteId)
    .sort((a, b) => b.fecha.localeCompare(a.fecha))
  const total = suma(suyas)
  return {
    ventas: suyas,
    compras: suyas.length,
    total,
    promedio: suyas.length ? total / suyas.length : 0,
    ultima: suyas[0]?.fecha,
    utilidad: sumaUtilidad(suyas),
    porCobrar: suyas
      .filter((v) => v.metodoPago === 'credito' && !v.pagado)
      .reduce((s, v) => s + v.total, 0),
  }
}

export function porMetodoPago(ventas: Venta[]) {
  const mapa = new Map<string, number>()
  for (const v of vigentes(ventas)) {
    mapa.set(v.metodoPago, (mapa.get(v.metodoPago) ?? 0) + v.total)
  }
  return mapa
}

/** Clientes nuevos en el periodo: primera compra dentro del rango. */
export function clientesNuevos(ventas: Venta[], inicio: Date) {
  const primera = new Map<string, string>()
  for (const v of vigentes(ventas)) {
    const actual = primera.get(v.clienteId)
    if (!actual || v.fecha < actual) primera.set(v.clienteId, v.fecha)
  }
  return [...primera.values()].filter((f) => new Date(f) >= inicio).length
}

export interface SeguimientoPendiente {
  clienteId: string
  clienteNombre: string
  /** Quien hizo la última venta: a esa persona le toca volver a ofrecer. */
  usuarioId: string
  usuarioNombre: string
  ultimaVenta: string
  diasDesde: number
  frecuenciaPedidoDias: number
}

/** Clientes que ya deberían haber vuelto a pedir, según la frecuencia
 *  que cada uno tiene configurada (Cliente.frecuenciaPedidoDias). Un
 *  cliente sin esa frecuencia definida simplemente no genera aviso —no
 *  hay una regla general que aplicarle. */
export function seguimientosPendientes(
  ventas: Venta[],
  clientes: Cliente[],
): SeguimientoPendiente[] {
  const salida: SeguimientoPendiente[] = []
  for (const cliente of clientes) {
    const frecuencia = cliente.frecuenciaPedidoDias
    if (!frecuencia || frecuencia <= 0) continue

    const ventasCliente = vigentes(ventas).filter((v) => v.clienteId === cliente.id)
    if (!ventasCliente.length) continue

    const ultima = ventasCliente.reduce((max, v) => (v.fecha > max.fecha ? v : max))
    const diasDesde = Math.floor((Date.now() - new Date(ultima.fecha).getTime()) / 86_400_000)
    if (diasDesde < frecuencia) continue

    salida.push({
      clienteId: cliente.id,
      clienteNombre: [cliente.nombre, cliente.apellido].filter(Boolean).join(' '),
      usuarioId: ultima.usuarioId,
      usuarioNombre: ultima.usuarioNombre,
      ultimaVenta: ultima.fecha,
      diasDesde,
      frecuenciaPedidoDias: frecuencia,
    })
  }
  return salida.sort((a, b) => b.diasDesde - a.diasDesde)
}

/* ------------------------------ Gastos ------------------------------ */

export const sumaGastos = (gastos: Gasto[]) => gastos.reduce((s, g) => s + g.monto, 0)

export const gastosEntre = (gastos: Gasto[], inicio: Date, fin: Date) =>
  gastos.filter((g) => {
    const f = new Date(g.fecha)
    return f >= inicio && f <= fin
  })

export function gastosPorCategoria(gastos: Gasto[]) {
  const mapa = new Map<string, number>()
  for (const g of gastos) mapa.set(g.categoria, (mapa.get(g.categoria) ?? 0) + g.monto)
  return [...mapa.entries()]
    .map(([categoria, total]) => ({ categoria, total }))
    .sort((a, b) => b.total - a.total)
}

/** Cuánto de lo gastado tiene factura con IVA (crédito fiscal) versus lo
 *  que no —boletas o compras sin documento tributario. */
export function ivaCreditoGastos(gastos: Gasto[], porcentajeIva: number) {
  let creditoFiscal = 0
  for (const g of gastos) {
    if (!g.afectoIva) continue
    // g.monto es lo que realmente se pagó (con IVA incluido); el
    // crédito es la parte de IVA dentro de ese total.
    creditoFiscal += g.monto - g.monto / (1 + porcentajeIva / 100)
  }
  return creditoFiscal
}

/* ------------------------ Rentabilidad anual ------------------------ */

export interface MesComparado {
  mes: string
  actual: number
  anterior: number
}

/** Utilidad de ventas mes a mes de un año calendario, comparada con el
 *  mismo mes del año anterior —lo que se pide para ver "cómo vamos"
 *  contra el año pasado, no solo contra el mes pasado. */
export function utilidadMensualComparada(ventas: Venta[], gastos: Gasto[], anio = new Date().getFullYear()) {
  const fmt = new Intl.DateTimeFormat('es-CL', { month: 'short' })
  const meses: MesComparado[] = Array.from({ length: 12 }, (_, m) => ({
    mes: fmt.format(new Date(anio, m, 1)).replace('.', ''),
    actual: 0,
    anterior: 0,
  }))

  for (const v of vigentes(ventas)) {
    const d = new Date(v.fecha)
    const y = d.getFullYear()
    const m = d.getMonth()
    if (y === anio) meses[m].actual += v.utilidad
    else if (y === anio - 1) meses[m].anterior += v.utilidad
  }
  for (const g of gastos) {
    const d = new Date(g.fecha)
    const y = d.getFullYear()
    const m = d.getMonth()
    if (y === anio) meses[m].actual -= g.monto
    else if (y === anio - 1) meses[m].anterior -= g.monto
  }

  const totalActual = meses.reduce((s, x) => s + x.actual, 0)
  const totalAnterior = meses.reduce((s, x) => s + x.anterior, 0)
  const deltaPct =
    totalAnterior !== 0
      ? ((totalActual - totalAnterior) / Math.abs(totalAnterior)) * 100
      : totalActual > 0
        ? 100
        : 0

  // Hasta qué mes ya pasó del año actual —para no comparar un año
  // completo contra uno que recién va por marzo.
  const hoy = new Date()
  const mesesTranscurridos = anio === hoy.getFullYear() ? hoy.getMonth() + 1 : 12

  return { meses, totalActual, totalAnterior, deltaPct, anio, mesesTranscurridos }
}
